import React, { useCallback, useEffect, useState } from 'react'
import { Form, Input } from 'antd'
import { Box, FormControlLabel, Paper, Switch, Typography } from '@mui/material'
import TimerIcon from '@mui/icons-material/Timer'

import type {
  BlockPayload,
  IBlockPayloadCountdown,
  ICountdownColors,
} from '../../../types'

const DEFAULT_COLORS: Required<ICountdownColors> = {
  bg: '#1e1b4b',
  bgGradient: true,
  bgFrom: '#1e1b4b',
  bgTo: '#312e81',
  bgAngle: 135,
  digitBg: '#6366f1',
  digitColor: '#ffffff',
  textColor: 'rgba(255,255,255,0.85)',
  iconColor: '#F97316',
}

function resolveBackground(c: ICountdownColors) {
  if (c.bgGradient && c.bgFrom && c.bgTo) {
    return `linear-gradient(${c.bgAngle ?? 135}deg, ${c.bgFrom} 0%, ${c.bgTo} 100%)`
  }
  return c.bg || DEFAULT_COLORS.bg
}

function rgbToHex(rgb: string): string {
  const m = rgb.match(/\d+/g)
  if (!m || m.length < 3) return '#000000'
  return '#' + m.slice(0, 3).map((n) => Number(n).toString(16).padStart(2, '0')).join('')
}

function ColorPickerField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (v: string) => void
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography variant="caption" fontWeight={600} color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          component="input"
          type="color"
          value={value.startsWith('rgba') || value.startsWith('rgb') ? rgbToHex(value) : value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          sx={{
            width: 36,
            height: 36,
            border: '2px solid',
            borderColor: 'divider',
            borderRadius: 1,
            cursor: 'pointer',
            p: 0.25,
            bgcolor: 'transparent',
            '&::-webkit-color-swatch-wrapper': { p: 0 },
            '&::-webkit-color-swatch': { borderRadius: 0.5, border: 'none' },
          }}
        />
        <Input
          size="small"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ flex: 1, fontFamily: 'monospace', fontSize: 12 }}
        />
      </Box>
    </Box>
  )
}

function useCountdown(expireAt: string) {
  const calcRemaining = useCallback(() => {
    const diff = new Date(expireAt).getTime() - Date.now()
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true }
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
      expired: false,
    }
  }, [expireAt])

  const [remaining, setRemaining] = useState(calcRemaining)

  useEffect(() => {
    setRemaining(calcRemaining())
    const id = setInterval(() => setRemaining(calcRemaining()), 1000)
    return () => clearInterval(id)
  }, [calcRemaining])

  return remaining
}

interface Props {
  payload: IBlockPayloadCountdown
  onChange: (payload: BlockPayload) => void
}

export const CountdownBlock: React.FC<Props> = ({ payload: p, onChange }) => {
  const expireAt = p?.expireAt ?? new Date(Date.now() + 86400000).toISOString()
  const title = p?.title ?? ''
  const expiredText = p?.expiredText ?? ''
  const colors: Required<ICountdownColors> = { ...DEFAULT_COLORS, ...p?.colors }

  const { days, hours, minutes, seconds, expired } = useCountdown(expireAt)

  const emit = (patch: Partial<IBlockPayloadCountdown>) =>
    onChange({ expireAt, title, expiredText, colors, ...patch })

  const updateColor = (key: keyof ICountdownColors, value: string | number | boolean) =>
    emit({ colors: { ...colors, [key]: value } })

  const localDatetime = (() => {
    const d = new Date(expireAt)
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  })()

  const pad2 = (n: number) => String(n).padStart(2, '0')
  const bgValue = resolveBackground(colors)

  const digitSx = {
    fontFamily: '"JetBrains Mono", "SF Mono", "Fira Code", monospace',
    fontWeight: 800,
    fontSize: { xs: 32, sm: 40 },
    lineHeight: 1,
    minWidth: { xs: 56, sm: 68 },
    py: { xs: 1.5, sm: 2 },
    px: 1,
    borderRadius: 2,
    textAlign: 'center',
    color: colors.digitColor,
    position: 'relative',
    overflow: 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: '8%',
      right: '8%',
      top: '50%',
      height: '1px',
      bgcolor: 'rgba(255,255,255,0.15)',
    },
  } as const

  const digitUnit = (value: string, label: string) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ ...digitSx, bgcolor: colors.digitBg, boxShadow: `0 4px 20px ${colors.digitBg}44` }}>
        {value}
      </Box>
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, color: colors.textColor, letterSpacing: 1, textTransform: 'uppercase', fontSize: 11 }}
      >
        {label}
      </Typography>
    </Box>
  )

  const colonSep = (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: { xs: 56, sm: 68 },
          py: { xs: 1.5, sm: 2 },
        }}
      >
        <Typography
          sx={{
            fontFamily: 'monospace',
            fontWeight: 800,
            fontSize: { xs: 28, sm: 36 },
            color: colors.textColor,
            opacity: 0.4,
            lineHeight: 1,
            userSelect: 'none',
          }}
        >
          :
        </Typography>
      </Box>
      <Typography variant="caption" sx={{ fontSize: 11, visibility: 'hidden' }}>
        &nbsp;
      </Typography>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Live Preview */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          textAlign: 'center',
          background: bgValue,
          border: '1px solid rgba(255,255,255,0.1)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {title && (
          <Typography
            variant="subtitle1"
            sx={{
              color: colors.textColor,
              fontWeight: 600,
              mb: 2.5,
              letterSpacing: 0.5,
              position: 'relative',
            }}
          >
            <TimerIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'text-bottom', color: colors.iconColor }} />
            {title}
          </Typography>
        )}
        {expired ? (
          <Typography
            variant="h5"
            sx={{
              color: colors.iconColor,
              fontWeight: 700,
              py: 3,
              position: 'relative',
            }}
          >
            {expiredText || 'หมดเวลาแล้ว!'}
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              gap: { xs: 1, sm: 1.5 },
              flexWrap: 'wrap',
              position: 'relative',
            }}
          >
            {digitUnit(String(days), 'วัน')}
            {colonSep}
            {digitUnit(pad2(hours), 'ชั่วโมง')}
            {colonSep}
            {digitUnit(pad2(minutes), 'นาที')}
            {colonSep}
            {digitUnit(pad2(seconds), 'วินาที')}
          </Box>
        )}
      </Paper>

      {/* Settings */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Form.Item label="วันเวลาหมดอายุ" style={{ marginBottom: 0 }}>
          <Input
            type="datetime-local"
            value={localDatetime}
            onChange={(e) => {
              const v = e.target.value
              if (!v) return
              emit({ expireAt: new Date(v).toISOString() })
            }}
          />
        </Form.Item>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Form.Item label="ข้อความหัวข้อ" style={{ marginBottom: 0 }}>
            <Input
              placeholder="โปรโมชั่นสิ้นสุดใน"
              value={title}
              onChange={(e) => emit({ title: e.target.value })}
            />
          </Form.Item>
          <Form.Item label="ข้อความเมื่อหมดเวลา" style={{ marginBottom: 0 }}>
            <Input
              placeholder="หมดเวลาแล้ว!"
              value={expiredText}
              onChange={(e) => emit({ expiredText: e.target.value })}
            />
          </Form.Item>
        </Box>
      </Box>

      {/* Color Settings */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
          ปรับแต่งสี
        </Typography>

        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          พื้นหลังหลัก
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <FormControlLabel
            control={
              <Switch
                checked={colors.bgGradient}
                onChange={(e) => updateColor('bgGradient', e.target.checked)}
                size="small"
              />
            }
            label={<Typography variant="caption">ใช้ Gradient</Typography>}
          />
        </Box>
        {colors.bgGradient ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 2 }}>
            <ColorPickerField
              label="สีเริ่มต้น"
              value={colors.bgFrom}
              onChange={(v) => updateColor('bgFrom', v)}
            />
            <ColorPickerField
              label="สีสิ้นสุด"
              value={colors.bgTo}
              onChange={(v) => updateColor('bgTo', v)}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary">
                องศา ({colors.bgAngle}°)
              </Typography>
              <Box
                component="input"
                type="range"
                min={0}
                max={360}
                value={colors.bgAngle}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateColor('bgAngle', Number(e.target.value))
                }
                sx={{ mt: 1 }}
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{ mb: 2, maxWidth: 200 }}>
            <ColorPickerField
              label="สีพื้นหลัง"
              value={colors.bg}
              onChange={(v) => updateColor('bg', v)}
            />
          </Box>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
          <ColorPickerField
            label="พื้นหลังตัวเลข"
            value={colors.digitBg}
            onChange={(v) => updateColor('digitBg', v)}
          />
          <ColorPickerField
            label="สีตัวเลข"
            value={colors.digitColor}
            onChange={(v) => updateColor('digitColor', v)}
          />
          <ColorPickerField
            label="สีข้อความ / Label"
            value={colors.textColor}
            onChange={(v) => updateColor('textColor', v)}
          />
          <ColorPickerField
            label="สี Icon นาฬิกา"
            value={colors.iconColor}
            onChange={(v) => updateColor('iconColor', v)}
          />
        </Box>
      </Paper>
    </Box>
  )
}
